import {
  AdminLearningContentRow,
  AdminLearningContentRowUpsert,
  AdminLearningContentSubCategory,
  AdminLearningContentTemplate,
} from '../types/domain';
import { supabase } from '../supabaseClient';
import {
  loadWorkspaceLearningContentRowsCache,
  loadWorkspaceLearningContentTemplatesCache,
  migrateWorkspaceLearningContentRowsCache,
  saveWorkspaceLearningContentRowsCache,
  saveWorkspaceLearningContentTemplatesCache,
} from '../utils/workspaceLearningContentCache';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  buildLearningContentScopeVersion,
  learningContentScopeVersionFromHead,
} from '../utils/learningContentCacheVersion';
import { notifySchoolProgramMutation } from '../utils/schoolDataCacheVersion';

const WORKSPACE_LEARNING_CONTENT_SELECT = [
  'id',
  'workspace_id',
  'grade_id',
  'grade_number',
  'sub_category',
  'template_name',
  'order_index',
  'publisher',
  'track_text',
  'seq_text',
  'unit_text',
  'learning_text',
  'page_text',
  'assistant_page_text',
  'target_lesson_text',
  'total_lesson_text',
  'material_text',
  'achievement_text',
  'record_cross_curricular_text',
  'record_memo_text',
  'record_evaluation_text',
  'created_at',
  'updated_at',
].join(', ');

function requireGradeId(gradeId: string): string {
  const gid = gradeId.trim();
  if (!gid) throw new Error('클래스를 선택해 주세요.');
  return gid;
}

async function fetchWorkspaceLearningContentRowsHead(
  workspaceId: string,
  gradeId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): Promise<string> {
  const scopeTemplate = templateName.trim();
  const { count, error: countError } = await supabase
    .from('workspace_learning_content_rows')
    .select('id', { count: 'exact', head: true })
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', scopeTemplate);
  if (countError) throw new Error(countError.message);
  if (!count) return learningContentScopeVersionFromHead(0, '');
  const { data, error } = await supabase
    .from('workspace_learning_content_rows')
    .select('updated_at')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', scopeTemplate)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (error) throw new Error(error.message);
  const maxUpdatedAt = (data?.[0] as { updated_at?: string } | undefined)?.updated_at ?? '';
  return learningContentScopeVersionFromHead(count, maxUpdatedAt);
}

/**
 * 학습내용 관리 전용.
 * LS 캐시가 있으면 head(`count:max(updated_at)`) 비교 후 동일하면 풀 fetch를 생략한다.
 */
export async function getWorkspaceLearningContentRows(
  workspaceId: string,
  gradeId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templateName: string,
  options?: { forceRefresh?: boolean }
): Promise<AdminLearningContentRow[]> {
  if (!workspaceId || !templateName.trim()) return [];
  const gid = requireGradeId(gradeId);
  const scopeTemplate = templateName.trim();
  const cached = loadWorkspaceLearningContentRowsCache(
    workspaceId,
    gid,
    gradeNumber,
    subCategory,
    scopeTemplate
  );
  if (!options?.forceRefresh && cached.found && cached.contentVersion) {
    try {
      const headVersion = await fetchWorkspaceLearningContentRowsHead(
        workspaceId,
        gid,
        gradeNumber,
        subCategory,
        scopeTemplate
      );
      if (cached.contentVersion === headVersion) {
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('기본학습내용등록·행·LS');
        }
        return cached.rows;
      }
    } catch {
      /* head 실패 시 풀 조회로 진행 */
    }
  }
  const { data, error } = await supabase
    .from('workspace_learning_content_rows')
    .select(WORKSPACE_LEARNING_CONTENT_SELECT)
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gid)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', scopeTemplate)
    .order('order_index', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  const rows = ((data ?? []) as unknown) as AdminLearningContentRow[];
  const contentVersion = buildLearningContentScopeVersion(rows);
  saveWorkspaceLearningContentRowsCache(
    workspaceId,
    gid,
    gradeNumber,
    subCategory,
    scopeTemplate,
    rows,
    { force: true, contentVersion }
  );
  return rows;
}

export async function replaceWorkspaceLearningContentRows(
  workspaceId: string,
  gradeId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templateName: string,
  rows: AdminLearningContentRowUpsert[]
): Promise<AdminLearningContentRow[]> {
  if (!workspaceId || !templateName.trim()) return [];
  const gid = requireGradeId(gradeId);
  const scopeTemplate = templateName.trim();
  const now = new Date().toISOString();
  const { error: deleteError } = await supabase
    .from('workspace_learning_content_rows')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gid)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', scopeTemplate);
  if (deleteError) throw new Error(deleteError.message);
  if (!rows.length) {
    await notifySchoolProgramMutation(workspaceId, ['learningContent']);
    saveWorkspaceLearningContentRowsCache(
      workspaceId,
      gid,
      gradeNumber,
      subCategory,
      scopeTemplate,
      [],
      { force: true }
    );
    return [];
  }
  const payload = rows.map(({ id: _ignored, ...row }, idx) => ({
    ...row,
    workspace_id: workspaceId,
    grade_id: gid,
    grade_number: gradeNumber,
    sub_category: subCategory,
    template_name: scopeTemplate,
    order_index: Number.isInteger(row.order_index) ? row.order_index : idx,
    /** 클라이언트에서 null/undefined가 섞여도 INSERT 시 NOT NULL 위반 방지 */
    created_at: now,
    updated_at: now,
  }));
  const { data, error } = await supabase
    .from('workspace_learning_content_rows')
    .insert(payload)
    .select(WORKSPACE_LEARNING_CONTENT_SELECT)
    .order('order_index', { ascending: true });
  if (error) throw new Error(error.message);
  await notifySchoolProgramMutation(workspaceId, ['learningContent']);
  const saved = ((data ?? []) as unknown) as AdminLearningContentRow[];
  saveWorkspaceLearningContentRowsCache(
    workspaceId,
    gid,
    gradeNumber,
    subCategory,
    scopeTemplate,
    saved,
    { force: true }
  );
  return saved;
}

const WORKSPACE_LEARNING_TEMPLATE_SELECT = [
  'id',
  'workspace_id',
  'grade_id',
  'grade_number',
  'sub_category',
  'template_name',
  'order_index',
  'created_at',
  'updated_at',
].join(', ');

async function refreshWorkspaceLearningContentTemplatesCache(
  workspaceId: string,
  gradeId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory
): Promise<void> {
  if (!workspaceId) return;
  const gid = requireGradeId(gradeId);
  const { data, error } = await supabase
    .from('workspace_learning_content_templates')
    .select(WORKSPACE_LEARNING_TEMPLATE_SELECT)
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gid)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .order('order_index', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  const list = ((data ?? []) as unknown) as AdminLearningContentTemplate[];
  saveWorkspaceLearningContentTemplatesCache(workspaceId, gid, gradeNumber, subCategory, list, {
    force: true,
  });
}

async function fetchWorkspaceLearningContentTemplatesHead(
  workspaceId: string,
  gradeId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory
): Promise<string> {
  const { count, error: countError } = await supabase
    .from('workspace_learning_content_templates')
    .select('id', { count: 'exact', head: true })
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory);
  if (countError) throw new Error(countError.message);
  if (!count) return learningContentScopeVersionFromHead(0, '');
  const { data, error } = await supabase
    .from('workspace_learning_content_templates')
    .select('updated_at')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (error) throw new Error(error.message);
  const maxUpdatedAt = (data?.[0] as { updated_at?: string } | undefined)?.updated_at ?? '';
  return learningContentScopeVersionFromHead(count, maxUpdatedAt);
}

export async function getWorkspaceLearningContentTemplates(
  workspaceId: string,
  gradeId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  options?: { forceRefresh?: boolean }
): Promise<AdminLearningContentTemplate[]> {
  if (!workspaceId) return [];
  const gid = requireGradeId(gradeId);
  const cached = loadWorkspaceLearningContentTemplatesCache(
    workspaceId,
    gid,
    gradeNumber,
    subCategory
  );
  if (!options?.forceRefresh && cached.found && cached.contentVersion) {
    try {
      const headVersion = await fetchWorkspaceLearningContentTemplatesHead(
        workspaceId,
        gid,
        gradeNumber,
        subCategory
      );
      if (cached.contentVersion === headVersion) {
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('기본학습내용등록·편제목록·LS');
        }
        return cached.templates;
      }
    } catch {
      /* head 실패 시 풀 조회로 진행 */
    }
  }
  const { data, error } = await supabase
    .from('workspace_learning_content_templates')
    .select(WORKSPACE_LEARNING_TEMPLATE_SELECT)
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gid)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .order('order_index', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  const list = ((data ?? []) as unknown) as AdminLearningContentTemplate[];
  const contentVersion = buildLearningContentScopeVersion(list);
  saveWorkspaceLearningContentTemplatesCache(workspaceId, gid, gradeNumber, subCategory, list, {
    force: true,
    contentVersion,
  });
  return list;
}

export async function createWorkspaceLearningContentTemplate(params: {
  workspaceId: string;
  gradeId: string;
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
  subCategory: AdminLearningContentSubCategory;
  templateName: string;
}): Promise<void> {
  const { workspaceId, gradeNumber, subCategory, templateName } = params;
  const gid = requireGradeId(params.gradeId);
  const name = templateName.trim();
  if (!workspaceId || !name) return;
  const now = new Date().toISOString();
  const { data: existing } = await supabase
    .from('workspace_learning_content_templates')
    .select('id')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gid)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', name)
    .maybeSingle();
  if (existing?.id) return;
  const { data: maxRow } = await supabase
    .from('workspace_learning_content_templates')
    .select('order_index')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gid)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .order('order_index', { ascending: false })
    .limit(1)
    .maybeSingle();
  const nextOrderIndex = typeof maxRow?.order_index === 'number' ? maxRow.order_index + 1 : 0;
  const { error } = await supabase.from('workspace_learning_content_templates').insert({
    workspace_id: workspaceId,
    grade_id: gid,
    grade_number: gradeNumber,
    sub_category: subCategory,
    template_name: name,
    order_index: nextOrderIndex,
    updated_at: now,
  });
  if (error) throw new Error(error.message);
  await notifySchoolProgramMutation(workspaceId, ['learningContent']);
  await refreshWorkspaceLearningContentTemplatesCache(workspaceId, gid, gradeNumber, subCategory);
}

export async function renameWorkspaceLearningContentTemplate(params: {
  workspaceId: string;
  gradeId: string;
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
  subCategory: AdminLearningContentSubCategory;
  fromTemplateName: string;
  toTemplateName: string;
}): Promise<void> {
  const { workspaceId, gradeNumber, subCategory, fromTemplateName, toTemplateName } = params;
  const gid = requireGradeId(params.gradeId);
  const fromName = fromTemplateName.trim();
  const toName = toTemplateName.trim();
  if (!workspaceId || !fromName || !toName || fromName === toName) return;
  const now = new Date().toISOString();
  const { data: duplicate } = await supabase
    .from('workspace_learning_content_templates')
    .select('id')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gid)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', toName)
    .maybeSingle();
  if (duplicate?.id) {
    throw new Error('이미 같은 이름의 편제가 있습니다.');
  }
  const { error: templateUpdateError } = await supabase
    .from('workspace_learning_content_templates')
    .update({ template_name: toName, updated_at: now })
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gid)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', fromName);
  if (templateUpdateError) throw new Error(templateUpdateError.message);
  const { error: rowsError } = await supabase
    .from('workspace_learning_content_rows')
    .update({ template_name: toName, updated_at: now })
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gid)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', fromName);
  if (rowsError) throw new Error(rowsError.message);

  migrateWorkspaceLearningContentRowsCache(
    workspaceId,
    gid,
    gradeNumber,
    subCategory,
    fromName,
    toName,
    now
  );
  await notifySchoolProgramMutation(workspaceId, ['learningContent']);
  await refreshWorkspaceLearningContentTemplatesCache(workspaceId, gid, gradeNumber, subCategory);
}

export async function reorderWorkspaceLearningContentTemplates(params: {
  workspaceId: string;
  gradeId: string;
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
  subCategory: AdminLearningContentSubCategory;
  orderedTemplateNames: string[];
}): Promise<void> {
  const { workspaceId, gradeNumber, subCategory, orderedTemplateNames } = params;
  const gid = requireGradeId(params.gradeId);
  if (!workspaceId) return;
  const names = orderedTemplateNames.map((name) => name.trim()).filter(Boolean);
  if (!names.length) return;
  const { data, error } = await supabase
    .from('workspace_learning_content_templates')
    .select('id, template_name, order_index')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gid)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{ id: string; template_name: string; order_index: number }>;
  const byName = new Map(rows.map((row) => [row.template_name.trim(), row]));
  const allNamesInCurrentOrder = [...rows]
    .sort((a, b) => a.order_index - b.order_index || a.id.localeCompare(b.id))
    .map((row) => row.template_name.trim());
  const used = new Set(names);
  const mergedNames = [...names, ...allNamesInCurrentOrder.filter((name) => !used.has(name))];

  const updates: Array<{ id: string; orderIndex: number; currentOrderIndex: number }> = [];
  mergedNames.forEach((name, idx) => {
    const row = byName.get(name);
    if (!row) return;
    if (row.order_index === idx) return;
    updates.push({ id: row.id, orderIndex: idx, currentOrderIndex: row.order_index });
  });
  if (!updates.length) return;

  // 1) 충돌 회피: 일단 모두 큰 임시 index로 이동
  for (const update of updates) {
    const { error: updateError } = await supabase
      .from('workspace_learning_content_templates')
      .update({ order_index: update.currentOrderIndex + 1000, updated_at: new Date().toISOString() })
      .eq('id', update.id);
    if (updateError) throw new Error(updateError.message);
  }
  // 2) 최종 순번 반영
  for (const update of updates) {
    const { error: updateError } = await supabase
      .from('workspace_learning_content_templates')
      .update({ order_index: update.orderIndex, updated_at: new Date().toISOString() })
      .eq('id', update.id);
    if (updateError) throw new Error(updateError.message);
  }
  await notifySchoolProgramMutation(workspaceId, ['learningContent']);
  await refreshWorkspaceLearningContentTemplatesCache(workspaceId, gid, gradeNumber, subCategory);
}

export async function deleteWorkspaceLearningContentTemplate(params: {
  workspaceId: string;
  gradeId: string;
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
  subCategory: AdminLearningContentSubCategory;
  templateName: string;
}): Promise<void> {
  const { workspaceId, gradeNumber, subCategory, templateName } = params;
  const gid = requireGradeId(params.gradeId);
  const name = templateName.trim();
  if (!workspaceId || !name) return;

  const { error: deleteRowsError } = await supabase
    .from('workspace_learning_content_rows')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gid)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', name);
  if (deleteRowsError) throw new Error(deleteRowsError.message);

  const { error: deleteTemplateError } = await supabase
    .from('workspace_learning_content_templates')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gid)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', name);
  if (deleteTemplateError) throw new Error(deleteTemplateError.message);

  await notifySchoolProgramMutation(workspaceId, ['learningContent']);
  await refreshWorkspaceLearningContentTemplatesCache(workspaceId, gid, gradeNumber, subCategory);
}
